import { useRef, type ButtonHTMLAttributes, type ReactNode } from "react";

interface Props extends ButtonHTMLAttributes<HTMLButtonElement> {
  children: ReactNode;
  /** Hur långt knappen "dras" mot pekaren (px), default 12 */
  strength?: number;
}

/**
 * Knapp som subtilt magnetiskt dras mot muspekaren när den är inom 60px.
 * Stängs av automatiskt på touch-enheter och vid prefers-reduced-motion.
 */
export function MagneticButton({ children, strength = 12, className, ...rest }: Props) {
  const ref = useRef<HTMLButtonElement>(null);

  function onMouseMove(e: React.MouseEvent<HTMLButtonElement>) {
    if (typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const x = e.clientX - rect.left - rect.width / 2;
    const y = e.clientY - rect.top - rect.height / 2;
    el.style.transform = `translate(${(x / rect.width) * strength}px, ${(y / rect.height) * strength}px)`;
  }

  function onMouseLeave() {
    const el = ref.current;
    if (!el) return;
    el.style.transform = "translate(0,0)";
  }

  return (
    <button
      ref={ref}
      onMouseMove={onMouseMove}
      onMouseLeave={onMouseLeave}
      className={className}
      style={{ transition: "transform 240ms cubic-bezier(.2,.7,.2,1)" }}
      {...rest}
    >
      {children}
    </button>
  );
}
